import { memo } from "react";
import { Link } from "react-router-dom";
import { usePWAInstall } from "../../hooks/Usepwainstall";
import styles from "./Footer.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();

const INTERNAL_LINKS = [
  { to: "/documentacao", label: "Documentação" },
  { to: "/sobre",        label: "Sobre" },
];

const CONTACT_INFO = [
  { icon: <EmailIcon />, label: "suporte@fluxoeasy.com.br", href: "mailto:suporte@fluxoeasy.com.br" },
  { icon: <PhoneIcon />, label: "(19) 98867-1786",          href: "tel:+5519988671786" },
];

const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://instagram.com/fluxoeasy",  icon: <InstagramIcon /> },
  { label: "WhatsApp",  href: "https://wa.me/5519988671786",       icon: <WhatsAppIcon /> },
  { label: "Telegram",  href: "https://t.me/fluxoeasy",           icon: <TelegramIcon /> },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const FooterSection = ({ title, children }) => (
  <div className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    {children}
  </div>
);

const FooterLink = ({ href, children, external = false }) =>
  external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
      {children}
    </a>
  ) : (
    <Link to={href} className={styles.link}>
      {children}
    </Link>
  );

// ─── Main component ───────────────────────────────────────────────────────────

const Footer = () => {
  const { canInstall, install } = usePWAInstall();

  return (
    <footer className={styles.footer} role="contentinfo">
      <div className={styles.inner}>

        {/* ── Brand column ── */}
        <div className={`${styles.section} ${styles.sectionBrand}`}>
          <div className={styles.brandText}>
            <p className={styles.brand}>FluxoEasy</p>
            <p className={styles.tagline}>
              Simplificando o controle de estoque e vendas para o seu negócio.
            </p>

            {/* Botão instalar — só aparece quando o browser suporta */}
            {canInstall && (
              <button
                type="button"
                onClick={install}
                className={styles.installBtn}
                aria-label="Instalar aplicativo FluxoEasy"
              >
                <DownloadIcon />
                Instalar aplicativo
              </button>
            )}
          </div>

          <div className={styles.socialRow} aria-label="Redes sociais">
            {SOCIAL_LINKS.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
                aria-label={`Acessar ${label}`}
                title={label}
              >
                {icon}
              </a>
            ))}
          </div>
        </div>

        {/* ── Links úteis ── */}
        <FooterSection title="Links úteis">
          <ul className={styles.list} role="list">
            {INTERNAL_LINKS.map(({ to, label }) => (
              <li key={to}>
                <FooterLink href={to}>{label}</FooterLink>
              </li>
            ))}
          </ul>
        </FooterSection>

        {/* ── Contato ── */}
        <FooterSection title="Contato">
          <ul className={styles.list} role="list">
            {CONTACT_INFO.map(({ icon, label, href }) => (
              <li key={href} className={styles.contactItem}>
                <span className={styles.contactIcon} aria-hidden="true">{icon}</span>
                <a href={href} className={styles.link}>{label}</a>
              </li>
            ))}
          </ul>
        </FooterSection>

      </div>

      {/* ── Bottom bar ── */}
      <div className={styles.bottom}>
        <p className={styles.copyright}>
          &copy; {CURRENT_YEAR} FluxoEasy. Todos os direitos reservados.
        </p>
        <p className={styles.madeWith}>
          Feito com cuidado para o seu negócio.
        </p>
      </div>
    </footer>
  );
};

export default memo(Footer);

// ─── Icons ────────────────────────────────────────────────────────────────────

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.93a16 16 0 0 0 5.98 5.98l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r=".5" fill="currentColor" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
      aria-hidden="true" focusable="false">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
      aria-hidden="true" focusable="false">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}